import React, { useState, useEffect } from "react";
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    TablePagination,
    Button,
    IconButton,
    Tooltip,
    TextField,
    MenuItem
} from "@mui/material";
import {
    AttachMoney,
    CheckCircle,
    Cancel,
    Paid,
    FilterList
} from "@mui/icons-material";
import api from '../../services/api.js';
import Preloader from '../../components/Preloader';
import { formatarMoeda, formatarData } from '../../utils/formatadores.js';

export default function GestaoComissoes() {
    const [comissoes, setComissoes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Filtros
    const [statusFilter, setStatusFilter] = useState('');
    // TODO: Add Representative Select Filter if needed

    useEffect(() => {
        carregarDados();
    }, [statusFilter]);

    const carregarDados = async () => {
        setLoading(true);
        try {
            const params = {};
            if (statusFilter) params.status = statusFilter;

            const res = await api.get('/comissao', { params });
            setComissoes(res.data.dados || []);
        } catch (error) {
            console.error('Erro ao carregar comissões:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateStatus = async (id, newStatus) => {
        if (!window.confirm(`Deseja alterar o status para ${newStatus}?`)) return;
        try {
            await api.put(`/comissao/${id}`, { en_status: newStatus });
            carregarDados(); // Reload
        } catch (error) {
            console.error("Erro ao atualizar status", error);
            alert("Erro ao atualizar status");
        }
    };

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, height: '100vh', overflow: 'auto' }}>
            <Typography variant="h5" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Paid sx={{ color: '#20ac9c', fontSize: 32 }} />
                Gestão Financeira de Comissões
            </Typography>

            {/* Filtros */}
            <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
                <TextField
                    select
                    label="Status"
                    size="small"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    sx={{ minWidth: 200 }}
                >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="PREVISTA">Prevista</MenuItem>
                    <MenuItem value="LIBERADA">Liberada</MenuItem>
                    <MenuItem value="PAGA">Paga</MenuItem>
                    <MenuItem value="CANCELADA">Cancelada</MenuItem>
                </TextField>
                <Button variant="outlined" startIcon={<FilterList />} onClick={carregarDados}>
                    Atualizar
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Data</TableCell>
                            <TableCell>Representante</TableCell>
                            <TableCell>Contrato/Origem</TableCell>
                            <TableCell>Valor Base</TableCell>
                            <TableCell>Comissão (%)</TableCell>
                            <TableCell align="center">Status</TableCell>
                            <TableCell align="center">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={7} align="center"><Preloader /></TableCell></TableRow>
                        ) : comissoes.length === 0 ? (
                            <TableRow><TableCell colSpan={7} align="center">Nenhuma comissão encontrada.</TableCell></TableRow>
                        ) : (
                            comissoes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((comissao) => (
                                <TableRow key={comissao.id_comissao} hover>
                                    <TableCell>{formatarData(comissao.createdAt)}</TableCell>
                                    <TableCell>{comissao.representante?.st_nome}</TableCell>
                                    <TableCell>
                                        {comissao.pagamentoOrigem?.contrato?.titular?.usuario?.st_nome || 'N/A'}
                                        <Typography variant="caption" display="block">
                                            {comissao.pagamentoOrigem?.contrato?.plano?.st_nome}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{formatarMoeda(comissao.nu_valor_base_centavos)}</TableCell>
                                    <TableCell>
                                        <Typography fontWeight="bold">{formatarMoeda(comissao.nu_valor_comissao_centavos)}</Typography>
                                        <Typography variant="caption">({comissao.nu_percentual_aplicado}%)</Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            label={comissao.en_status}
                                            color={
                                                comissao.en_status === 'PAGA' ? 'success' :
                                                    comissao.en_status === 'LIBERADA' ? 'info' :
                                                        comissao.en_status === 'CANCELADA' ? 'error' : 'warning'
                                            }
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        {comissao.en_status === 'PREVISTA' && (
                                            <Tooltip title="Liberar Manualmente">
                                                <IconButton size="small" color="info" onClick={() => handleUpdateStatus(comissao.id_comissao, 'LIBERADA')}>
                                                    <CheckCircle />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {comissao.en_status === 'LIBERADA' && (
                                            <Tooltip title="Marcar como PAGA">
                                                <IconButton size="small" color="success" onClick={() => handleUpdateStatus(comissao.id_comissao, 'PAGA')}>
                                                    <Paid />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {(comissao.en_status === 'PREVISTA' || comissao.en_status === 'LIBERADA') && (
                                            <Tooltip title="Cancelar">
                                                <IconButton size="small" color="error" onClick={() => handleUpdateStatus(comissao.id_comissao, 'CANCELADA')}>
                                                    <Cancel />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={comissoes.length}
                    page={page}
                    onPageChange={(e, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </TableContainer>
        </Box>
    );
}
